import { useCallback, useEffect, useRef, useState } from "react";
import {
  getClassroomHandoverEntry,
  saveClassroomHandoverGroupingDecision,
} from "../services/APIClient";
import { hasClassroomJourneyProgress } from "../utils/classroomHandoverProgress";
import { loadDifferentGroupingsGateway } from "../features/classroomHandover/differentGroupings/differentGroupingsGateway";

export const getGroupingDecisionIdempotencyKey = (
  schoolId,
  academicYear,
  progressionKey,
  expectedVersion
) => `classroom-handover:${schoolId}:${academicYear}:${progressionKey}:same_groups:${expectedVersion}`;

const getStatus = (error) => error?.response?.status;
const MUTATION_STATUS_BY_RESPONSE = {
  403: "mutation_forbidden",
  404: "mutation_not_found",
  409: "stale",
};

export default function useClassroomHandover({ schoolId, academicYear, refreshKey }) {
  const [entry, setEntry] = useState(null);
  const [entryRevision, setEntryRevision] = useState(0);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [differentGroupingsAvailability, setDifferentGroupingsAvailability] = useState("loading");
  const [differentGroupingsTask, setDifferentGroupingsTask] = useState(null);
  const [differentGroupingsTaskStatus, setDifferentGroupingsTaskStatus] = useState("idle");
  const loadGeneration = useRef(0);
  const backgroundRefreshGeneration = useRef(null);
  const completedJourneyRead = useRef(false);
  const retryAttempt = useRef(null);
  const reconciliationAttempt = useRef(null);

  const readAuthoritativeEntry = useCallback(async () => {
    return getClassroomHandoverEntry(schoolId, academicYear);
  }, [academicYear, schoolId]);

  const load = useCallback(async ({ background = false } = {}) => {
    if (!schoolId || !academicYear) return null;
    const generation = ++loadGeneration.current;
    backgroundRefreshGeneration.current = background ? generation : null;
    if (!background) {
      completedJourneyRead.current = false;
      setStatus("loading");
      setDifferentGroupingsTaskStatus("loading");
    }
    setError(null);
    try {
      const nextEntry = await readAuthoritativeEntry();
      if (generation !== loadGeneration.current) return null;
      setEntry(nextEntry);
      setEntryRevision((current) => current + 1);
      setStatus(nextEntry?.setupRequired || hasClassroomJourneyProgress(nextEntry) ? "ready" : "not_required");
      return nextEntry;
    } catch (nextError) {
      if (generation !== loadGeneration.current) return null;
      completedJourneyRead.current = false;
      setDifferentGroupingsTaskStatus("error");
      setDifferentGroupingsAvailability("disabled");
      const responseStatus = getStatus(nextError);
      if (responseStatus === 404) {
        setEntry(null);
        setStatus("not_found");
        return null;
      }
      setStatus(responseStatus === 403 ? "read_forbidden" : "read_error");
      setError(nextError);
      throw nextError;
    }
  }, [academicYear, readAuthoritativeEntry, schoolId]);

  useEffect(() => {
    load().catch(() => {});
    return () => { loadGeneration.current += 1; };
  }, [load, refreshKey]);

  useEffect(() => {
    const refresh = () => {
      load({ background: completedJourneyRead.current }).catch(() => {});
    };
    window.addEventListener("focus", refresh);
    window.addEventListener("pageshow", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("pageshow", refresh);
    };
  }, [load]);

  useEffect(() => {
    let cancelled = false;
    const generation = loadGeneration.current;
    const background = backgroundRefreshGeneration.current === generation;
    if (!schoolId || !academicYear || !entry?.academicYear) {
      completedJourneyRead.current = false;
      setDifferentGroupingsTask(null);
      setDifferentGroupingsTaskStatus("idle");
      return undefined;
    }

    if (!background) setDifferentGroupingsTaskStatus("loading");
    loadDifferentGroupingsGateway({ progressionPoints: entry.progressionPoints })
      .then(async (gateway) => {
        let task = null;
        if (gateway.available === true && gateway.loadTask) {
          task = await gateway.loadTask(schoolId, entry.academicYear);
        } else if (gateway.available === true && gateway.checkAvailability) {
          await gateway.checkAvailability(schoolId, academicYear);
        }
        if (!cancelled && generation === loadGeneration.current) {
          setDifferentGroupingsTask(task);
          setDifferentGroupingsTaskStatus("ready");
          setDifferentGroupingsAvailability(gateway.available === true ? "enabled" : "disabled");
          completedJourneyRead.current = true;
          if (backgroundRefreshGeneration.current === generation) backgroundRefreshGeneration.current = null;
        }
      })
      .catch(() => {
        if (!cancelled && generation === loadGeneration.current) {
          completedJourneyRead.current = false;
          setDifferentGroupingsTask(null);
          setDifferentGroupingsTaskStatus("error");
          setDifferentGroupingsAvailability("disabled");
          if (backgroundRefreshGeneration.current === generation) backgroundRefreshGeneration.current = null;
        }
      });
    return () => {
      cancelled = true;
    };
  }, [academicYear, entry?.academicYear, entry?.progressionPoints, entryRevision, schoolId]);

  const resolveDifferentGroupingsDecision = useCallback(async (
    progressionKey,
    authoritativeEntry
  ) => {
    const gateway = await loadDifferentGroupingsGateway({
      progressionPoints: authoritativeEntry?.progressionPoints,
    });
    if (!gateway.available) return;

    const task = await gateway.loadTask(schoolId, academicYear);
    if (!task.selectedProgressionPointIds.includes(progressionKey)) return;
    if (!gateway.resolveAsSameGrouping) return authoritativeEntry;
    await gateway.resolveAsSameGrouping(task.taskReference, progressionKey);
  }, [academicYear, schoolId]);

  const reconcileOrRecover = useCallback(async (progressionKey, authoritativeEntry) => {
    try {
      await resolveDifferentGroupingsDecision(progressionKey, authoritativeEntry);
      reconciliationAttempt.current = null;
      return authoritativeEntry;
    } catch (nextError) {
      reconciliationAttempt.current = { progressionKey, authoritativeEntry };
      setError(nextError);
      setStatus("reconciliation_error");
      throw nextError;
    }
  }, [resolveDifferentGroupingsDecision]);

  const persist = useCallback(async (attempt) => {
    setStatus("saving");
    setError(null);
    try {
      await saveClassroomHandoverGroupingDecision(schoolId, attempt);
      const nextEntry = await getClassroomHandoverEntry(schoolId, academicYear);
      retryAttempt.current = null;
      setEntry(nextEntry);
      setEntryRevision((current) => current + 1);
      setStatus("ready");
      return nextEntry;
    } catch (nextError) {
      retryAttempt.current = attempt;
      setStatus(MUTATION_STATUS_BY_RESPONSE[getStatus(nextError)] || "mutation_error");
      setError(nextError);
      throw nextError;
    }
  }, [academicYear, schoolId]);

  const submitDecision = useCallback(async (progressionKey) => {
    const progressionPoint = entry?.progressionPoints?.find(
      (point) => point.key === progressionKey
    );
    if (progressionPoint?.groupingDecision === "same_groups") {
      return reconcileOrRecover(progressionKey, entry);
    }
    const expectedVersion = progressionPoint?.version ?? 0;
    const nextEntry = await persist({
      academicYear,
      progressionKey,
      decision: "same_groups",
      expectedVersion,
      idempotencyKey: getGroupingDecisionIdempotencyKey(
        schoolId,
        academicYear,
        progressionKey,
        expectedVersion
      ),
    });
    return reconcileOrRecover(progressionKey, nextEntry);
  }, [academicYear, entry, persist, reconcileOrRecover, schoolId]);

  const retryReconciliation = useCallback(async () => {
    const attempt = reconciliationAttempt.current;
    if (!attempt) return null;
    setStatus("reconciling");
    setError(null);
    try {
      const result = await reconcileOrRecover(
        attempt.progressionKey,
        attempt.authoritativeEntry
      );
      setStatus("ready");
      return result;
    } catch (nextError) {
      throw nextError;
    }
  }, [reconcileOrRecover]);

  const retryDecision = useCallback(() => {
    if (reconciliationAttempt.current) return retryReconciliation();
    if (!retryAttempt.current) return load();
    return persist(retryAttempt.current);
  }, [load, persist, retryReconciliation]);

  return {
    entry,
    status,
    error,
    load,
    submitDecision,
    retryDecision,
    differentGroupingsEnabled: differentGroupingsAvailability === "enabled",
    differentGroupingsAvailability,
    differentGroupingsTask,
    differentGroupingsTaskStatus,
  };
}
